import {
  put, call, select, all,
} from 'lib/redux';
import getDuplicateReportsService from 'services/getDuplicateReports.service';
import {
  ADDRESS_TYPES, ADDRESS_MAPPER, EMPTY_ADDRESS, LOADING_ADDRESS,
  ERROR_LOADING_ADDRESS,
} from 'features/placeOrder/OrderAddresses.constants';
import {
  getInitParametersSaga, getUserProductsSaga, getOrderPreferencesSaga, getSubscriptionDashboardDataSaga,
} from 'features/userData/UserData.saga';
import geocodeService from 'services/geocode.service';
import * as types from 'constants/ActionTypes';
import reverseGeocodeService from 'services/reverseGeocode.service';
import { processSearchInput } from 'utils/search.utils';
import getAvailableSolarProductsService from 'services/getAvailableSolarProducts.service';
import { convertFileToBase64 } from 'utils/app.utils';
import i18n from 'i18n';
import uploadResourceFileService from 'services/uploadResourceFile.service';
import {
  FILE_TYPES, FILE_FORMATS, PRODUCT_IDS, MEASUREMENT_REQUEST_TYPE_IDS,
} from 'constants/AppConstants';
import { ENABLE_GEOMNI_ESX_ADDON_LOGIC } from 'constants/FeatureFlag.constants';
import getOrderHistoryReportDetailsService from 'services/getOrderHistoryReportDetails.service';
import {
  isReportCancellable,
} from 'features/orderHistory/OrderHistory.utils';
import { fetchOrderClosureReasonsSaga } from 'features/cancel-order-dialog/CancelOrder.saga';
import {
  fetchOrderHistoryReportFileTypesSaga,
  fetchAvailableReportActions,
  getAvailableReportActions,
  fetchSubscriptionReportDetailsSaga,
} from 'features/orderHistory/OrderHistory.saga';
import checkImageCoverageService from 'services/checkImageCoverage.service';
import ErrorCodes from 'constants/ErrorCodes';
import {
  isEntitledForImageAvailabiltySelector,
  featureFlagSelector,
  isSubscriptionDashboardV2EnabledSelector, // #genai
} from 'utils/appSelectors.utils';
import { isAuthorized } from 'utils/auth.utils';
import { logError } from 'utils/logger.utils';
import {
  getDefaultMeasurementRequestTypes, getGeocodedAddressFromAddressResponse, getProductFromId,
  getPrimarySubscriptionProductsFromUserProducts,
  extractZipCodeFromText, isGeocodeUnresolvable, buildZipFallbackGeocodedAddress,
  hasUsableGeocodeCoordinates, stripUiAddressFlags,
} from './Order.utils';
import {
  PRIMARY_SUBSCRIPTION_PRODUCTS,
  SUBSCRIPTION_DELIVERY_PRODUCT_IDS,
  FULL_HOUSE_PRODUCT_ID,
  getProductIdsBySubscriptionSkuName,
} from './Order.constants';
import { ESX_COMPATIBLE_PRODUCTS } from './components/Product/ProductList.constants';
import { getSolarProductIds, filterNotSetProducts } from './components/Product/ProductList.util';
import {
  checkImageCoverageStart, checkImageCoverageSuccess, checkImageCoverageError, checkImageCoverageReset,
  deleteMapPoint, updateMapPointAddress, updateMapPointLocation,
  setShowMap, srsBranchEmployeeInfoReset, setSelectedMeasurementRequest, updateSelectedProduct,
} from './Order.actions';

const selectSolarValues = ({
  userData: { userProducts }, orderAddresses: {
    geocodedAddress: {
      latitude, longitude,
    } = {},
  },
}) => ({
  latitude,
  longitude,
  productList: getSolarProductIds(userProducts).filter((product) => product !== PRODUCT_IDS.EagleViewInformEssentialsPlus),
});

export function* mergePrimarySubscriptionProductsSaga() {
  const { userData } = yield select((state) => state) || {};
  const subscriptions = userData?.subscriptionDashboard?.subscriptions ?? [];
  const userProducts = userData?.userProducts;
  const selectedPrimaryProduct = yield select(({ order }) => order?.selectedPrimaryProduct);
  const isEditOrder = yield select(({ order }) => Boolean(order?.isEditOrder));

  const usageByProductId = {};
  let bundleFromApi = null;
  subscriptions.forEach((sub) => {
    const { subscriptionSkuName } = sub;
    if (!subscriptionSkuName) return;
    const productIds = getProductIdsBySubscriptionSkuName(subscriptionSkuName);
    if (!productIds.length) return; // delivery / non-subscription names or unknown plan name

    const usedQty = sub.usedQty ?? 0;
    const includedQty = sub.includedQty ?? 0;
    const isSubscription = includedQty > 0;
    const overageRatePerUnit = sub.overageRatePerUnit ?? 0;

    if (productIds.length > 1) {
      bundleFromApi = {
        productIds,
        used: usedQty,
        total: includedQty,
        isSubscription,
        overageRatePerUnit,
        subscriptionSkuName,
      };
      productIds.forEach((id) => {
        usageByProductId[id] = {
          usedQty,
          includedQty,
          isSubscription,
          overageRatePerUnit,
          subscriptionSkuName,
        };
      });
      return;
    }

    const [productId] = productIds;
    usageByProductId[productId] = {
      usedQty,
      includedQty,
      isSubscription,
      overageRatePerUnit,
      subscriptionSkuName,
    };
  });

  const { activeProductIds, productByProductId } = getPrimarySubscriptionProductsFromUserProducts(userProducts);
  const bundleUsage = bundleFromApi?.isSubscription
    ? { used: bundleFromApi.used ?? 0, total: bundleFromApi.total ?? 0 }
    : null;
  const isBundlePlan = Boolean(bundleUsage && (bundleUsage.total ?? 0) > 0);
  const bundleProductIdSet = isBundlePlan ? new Set(bundleFromApi.productIds) : null;

  const primarySubscriptionProductsWithUsage = PRIMARY_SUBSCRIPTION_PRODUCTS.map((product) => {
    const isProductActive = activeProductIds.has(product.productId);
    const fromProductsApi = productByProductId[product.productId];
    const fromSubscription = isBundlePlan && bundleProductIdSet && !bundleProductIdSet.has(product.productId)
      ? null
      : (usageByProductId[product.productId] ?? null);

    const description = product.description ?? '';
    // #genai nested ternary split into a lazily evaluated helper; same branches and order
    const getApiPriceLabel = () => (fromProductsApi.PriceMin === fromProductsApi.PriceMax
      ? String(fromProductsApi.PriceMin ?? '')
      : `${fromProductsApi.PriceMin ?? ''}-${fromProductsApi.PriceMax ?? ''}`);
    const price = isProductActive && fromProductsApi ? getApiPriceLabel() : (product.price ?? '');
    const name = isProductActive && fromProductsApi ? (fromProductsApi.Name ?? product.name) : product.name;

    const webDelivery = fromProductsApi?.WebDeliveryProducts ?? [];
    const allowedDeliveries = webDelivery
      .filter((d) => SUBSCRIPTION_DELIVERY_PRODUCT_IDS.includes(d.ProductID))
      .map((d) => ({
        productId: d.ProductID,
        name: d.Name ?? '',
        price: d.PriceMax ?? d.PriceMin ?? 0,
      }));
    const esxProductFromApi = productByProductId[PRODUCT_IDS.ESX];
    const isEsxCompatible = Boolean(esxProductFromApi)
      && Array.isArray(fromProductsApi?.CompatibleProducts)
      && fromProductsApi.CompatibleProducts.includes(PRODUCT_IDS.ESX);
    const esxPrice = isEsxCompatible && esxProductFromApi
      ? String(esxProductFromApi.PriceMax ?? esxProductFromApi.PriceMin ?? 0)
      : '';

    const merged = {
      ...product,
      name,
      description,
      price,
      isProductActive,
      allowedDeliveries,
      isEsxCompatible,
      esxPrice,
    };
    if (!fromSubscription) {
      return { ...merged, isSubscription: false };
    }
    return {
      ...merged,
      usedQty: fromSubscription.usedQty,
      includedQty: fromSubscription.includedQty,
      isSubscription: fromSubscription.isSubscription,
      overageRatePerUnit: fromSubscription.overageRatePerUnit,
      subscriptionSkuName: fromSubscription.subscriptionSkuName ?? merged.subscriptionSkuName,
    };
  });

  const withUnitBadges = primarySubscriptionProductsWithUsage.map((p) => {
    if (!p.isSubscription) return p;
    // #genai nested ternary flattened: Full House is 2 units only on a bundle plan, everything else 1
    const isFullHouseOnBundlePlan = isBundlePlan && p.productId === FULL_HOUSE_PRODUCT_ID;
    const unitBadgeCount = isFullHouseOnBundlePlan ? 2 : 1;
    return { ...p, unitBadgeCount };
  });

  yield put({
    type: types.PRIMARY_SUBSCRIPTION_PRODUCTS_WITH_USAGE_SUCCESS,
    primarySubscriptionProductsWithUsage: withUnitBadges,
    subscriptionPlanMode: isBundlePlan ? 'bundle' : 'single',
    bundleUsage,
  });

  // New subscription order flow: default measurement instruction to "All Structures on Parcel" for place order API
  const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector);
  const hasSubscriptionProducts = withUnitBadges.some((p) => p.isSubscription);
  if (isSubscriptionDashboardV2Enabled && hasSubscriptionProducts) {
    yield put(setSelectedMeasurementRequest(MEASUREMENT_REQUEST_TYPE_IDS.AllStructuresOnParcel));

    // Subscription V2: respect Products API IsDefault for active subscription cards.
    const activeSubscriptionIds = new Set(
      withUnitBadges
        .filter((p) => p.isProductActive)
        .map((p) => Number(p.productId)),
    );
    const structureKeys = Object.keys(userProducts || {}).filter((k) => userProducts?.[k]?.PrimaryProducts);
    const defaultPrimary = structureKeys
      .flatMap((structureKey) => userProducts[structureKey]?.PrimaryProducts || [])
      .find((p) => p?.IsDefault && activeSubscriptionIds.has(Number(p.ProductID)));

    const defaultPrimaryId = Number(defaultPrimary?.ProductID);
    if (!isEditOrder && defaultPrimary && Number(selectedPrimaryProduct?.ProductID) !== defaultPrimaryId) {
      const defaultDeliveryId = defaultPrimary.WebDeliveryProducts?.find((d) => d.IsDefault)?.ProductID
        ?? defaultPrimary.WebDeliveryProducts?.[0]?.ProductID
        ?? PRODUCT_IDS.RegularDelivery;
      yield put(updateSelectedProduct({
        selectedPrimaryProduct: defaultPrimary,
        selectedPrimaryDeliveryProduct: defaultDeliveryId,
        selectedWallsProduct: null,
        selectedWallsDeliveryProduct: null,
        selectedOtherProducts: {},
      }));
    }
  }
}

export function* getSolarProductsAvailabilitySaga() {
  try {
    const requestBody = yield select(selectSolarValues);
    if (Object.values(requestBody).every(Boolean) && requestBody.productList.length > 0) {
      yield put({ type: types.SET_PRODUCT_LOADING });
      const response = yield call(getAvailableSolarProductsService, requestBody);

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const solarProductsAvailability = response.json
        ?.availabilityStatus
        ?.reduce((availability, { productId, isAvailable }) => ({ ...availability, [productId]: isAvailable }), {}) || {};

      yield put({ type: types.SOLAR_PRODUCTS_AVAILABILITY_SUCCESS, solarProductsAvailability });
    }
  } catch (error) {
    logError({
      message: 'Error in getSolarProductsAvailabilitySaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put({ type: types.SOLAR_PRODUCTS_AVAILABILITY_ERROR, error: error.message });
  }
}

export function* fetchDuplicateReportDetailsSaga(duplicateReportIds, companyId, orgNodeId) {
  try {
    const {
      ok: duplicateReportDetailsStatus,
      json: duplicateReportDetails,
    } = yield call(getOrderHistoryReportDetailsService, duplicateReportIds, companyId, orgNodeId);

    if (!duplicateReportDetailsStatus) {
      const error = new Error(duplicateReportDetails.errorDescription);
      error.errorCode = duplicateReportDetails.errorCode;
      throw error;
    }

    return duplicateReportDetails;
  } catch (error) {
    logError({
      message: 'Error in fetchDuplicateReportDetailsSaga',
      error: error.message,
      errorCode: error.errorCode,
      duplicateReportIds,
      companyId,
      orgNodeId,
    });
    throw new Error(error.message);
  }
}

export function* getDuplicateReportsSaga() {
  try {
    const { companyId, orgNodeId, geocodedAddress } = yield select(({
      userData: { initParameters },
      orderAddresses,
    }) => ({ ...initParameters, geocodedAddress: orderAddresses.geocodedAddress }));

    const duplicateReportIdsResponse = yield call(
      getDuplicateReportsService,
      stripUiAddressFlags(geocodedAddress),
    );
    if (!duplicateReportIdsResponse.ok) {
      const error = new Error(duplicateReportIdsResponse.errorDescription);
      error.errorCode = duplicateReportIdsResponse.errorCode;
      throw error;
    }

    const { json: { reportIds: duplicateReportIds } } = duplicateReportIdsResponse;
    if (duplicateReportIds?.length === 0) return;

    const { duplicateReportDetails, availableActions } = yield all({
      availableActions: fetchAvailableReportActions(duplicateReportIds),
      duplicateReportDetails: fetchDuplicateReportDetailsSaga(duplicateReportIds, companyId, orgNodeId),
    });

    const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector);

    yield all([
      fetchOrderHistoryReportFileTypesSaga(duplicateReportDetails),
      isSubscriptionDashboardV2Enabled && fetchSubscriptionReportDetailsSaga(duplicateReportIds),
      // Fetch closure reasons only if cancellable reports are present
      ...(duplicateReportDetails.some(({ statusID, subStatusID }) => isReportCancellable(statusID, subStatusID))
        ? [fetchOrderClosureReasonsSaga()] : []),
      availableActions && getAvailableReportActions(duplicateReportDetails, duplicateReportIds, availableActions),
    ]);
    yield put({ type: types.ORDER_PAGE_DUPLICATE_REPORTS_SUCCESS, data: duplicateReportDetails });
  } catch (error) {
    logError({
      message: 'Error in getDuplicateReportsSaga',
      error: error.message,
      errorCode: error.errorCode,
    });
    yield put({ type: types.ORDER_PAGE_DUPLICATE_REPORTS_ERROR, error: error.message });
  }
}

export function* checkImageCoverageSaga({ latitude, longitude }) {
  const { myevRefreshImageAvailability } = yield select(({ featureFlag, userEntitlements }) => ({
    myevRefreshImageAvailability: featureFlag.myevRefreshImageAvailability,
    entitlements: userEntitlements,
  }));
  const isEntitledForImageAvailabilty = yield select(isEntitledForImageAvailabiltySelector);
  if (isEntitledForImageAvailabilty && myevRefreshImageAvailability) {
    yield put(checkImageCoverageReset());
    yield put(checkImageCoverageStart());
    try {
      const response = yield call(checkImageCoverageService, latitude, longitude);
      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      yield put(checkImageCoverageSuccess({
        latitude, longitude, isImageCovered: response.json.isImageCovered,
      }));
      if (!response.json.isImageCovered) {
        yield put(setShowMap(false));
      }
    } catch (error) {
      logError({
        message: 'Error in checkImageCoverageSaga',
        error: error.message,
        errorCode: error.errorCode,
        latitude,
        longitude,
      });
      yield put(checkImageCoverageError(error.message));
    }
  }
}

const createGeocodeError = (response) => {
  const error = new Error(response.json.errorDescription);
  error.errorCode = response.json.errorCode;
  return error;
};

function* geocodeZipFallback(extractedZip, country, input, userEnteredAddress) {
  const zipResponse = yield call(geocodeService, extractedZip, country);
  if (!zipResponse.ok || !hasUsableGeocodeCoordinates(zipResponse.json?.lat, zipResponse.json?.lon)) {
    return null;
  }
  return buildZipFallbackGeocodedAddress({
    zipGeocodeResponse: zipResponse.json,
    originalAddressText: input,
    originalUserEnteredAddress: { ...userEnteredAddress, zip: extractedZip },
    extractedZip,
  });
}

const withZipFallbackFlag = (geocodedAddress) => (
  hasUsableGeocodeCoordinates(geocodedAddress?.latitude, geocodedAddress?.longitude)
    ? { ...geocodedAddress, usedZipFallback: true }
    : geocodedAddress
);

function* resolveGeocodedAddress({
  response, isInputAddress, input, country, extractedZip, userEnteredAddress,
}) {
  if (!response.ok) {
    if (isInputAddress && extractedZip) {
      const zipFallback = yield call(geocodeZipFallback, extractedZip, country, input, userEnteredAddress);
      if (zipFallback) return zipFallback;
    }
    throw createGeocodeError(response);
  }

  const address = response.json;
  const geocodedAddress = getGeocodedAddressFromAddressResponse(address);
  if (!isInputAddress || !isGeocodeUnresolvable(address)) return geocodedAddress;

  if (extractedZip) {
    const zipFallback = yield call(geocodeZipFallback, extractedZip, country, input, userEnteredAddress);
    if (zipFallback) return zipFallback;
  }
  return withZipFallbackFlag(geocodedAddress);
}

export function* getGeocodeSaga({ searchInput, country, resetSearchInput }) {
  try {
    const { myevRefreshImageAvailability } = yield select(({
      featureFlag,
    }) => ({ myevRefreshImageAvailability: featureFlag.myevRefreshImageAvailability }));
    const { isInputAddress, input } = processSearchInput(searchInput);
    const addressSearchText = yield select(({ orderAddresses }) => orderAddresses.addressSearchText);
    let response;
    const userEnteredAddress = {
      address: '',
      address2: '',
      addressTypeId: ADDRESS_TYPES.User,
      city: '',
      country,
      county: null,
      latitude: 0,
      longitude: 0,
      mapperUsedId: ADDRESS_MAPPER.NotSet,
      state: '',
      verificationResultTypeId: 2,
      verifierUsedId: ADDRESS_MAPPER.NotSet,
      zip: '',
    };
    if (isInputAddress) {
      userEnteredAddress.address = input;
      response = yield call(geocodeService, input, country);
    } else {
      const [latitude, longitude] = input;
      userEnteredAddress.latitude = Number(latitude);
      userEnteredAddress.longitude = Number(longitude);
      response = yield call(reverseGeocodeService, input, country);
    }

    const extractedZip = extractZipCodeFromText(isInputAddress ? input : '', country)
      || extractZipCodeFromText(addressSearchText, country);
    const geocodedAddress = yield call(resolveGeocodedAddress, {
      response, isInputAddress, input, country, extractedZip, userEnteredAddress,
    });
    // Sync zip onto local userEnteredAddress after zip fallback (avoid mutating saga params).
    if (geocodedAddress?.usedZipFallback && geocodedAddress.zip) {
      userEnteredAddress.zip = geocodedAddress.zip;
    }

    const isEntitledForImageAvailabilty = yield select(isEntitledForImageAvailabiltySelector);
    if (isEntitledForImageAvailabilty && myevRefreshImageAvailability) {
      yield put(checkImageCoverageReset());
    }

    yield put({ type: types.OPEN_MAP_TOAST, toastAlertMessage: 'GeocodeSuccess' });
    yield put({
      type: types.GEOCODE_SUCCESS, geocodedAddress, resetSearchInput, userEnteredAddress,
    });
    if (isAuthorized()) {
      yield all([
        call(getDuplicateReportsSaga),
        call(getSolarProductsAvailabilitySaga),
        myevRefreshImageAvailability && call(checkImageCoverageSaga, {
          latitude: geocodedAddress.latitude,
          longitude: geocodedAddress.longitude,
        }),
      ]);
    }
  } catch (error) {
    logError({
      message: 'Error in getGeocodeSaga',
      error: error.message,
      errorCode: error.errorCode,
      searchInput,
      country,
    });
    yield put({ type: types.OPEN_MAP_TOAST, toastAlertMessage: 'GeocodeError' });
    yield put({ type: types.GEOCODE_ERROR, error: error.message });
  }
}

export function* loadOrderPageSaga() {
  if (!isAuthorized()) return;
  yield put(srsBranchEmployeeInfoReset);

  const isEditOrder = yield select(({ order }) => order.isEditOrder);
  if (isEditOrder) {
    yield put({ type: types.RESET_EDIT_ORDER });
  } else {
    try {
      yield all([
        getOrderPreferencesSaga(),
        getInitParametersSaga(),
        getUserProductsSaga(),
      ]);
      const isSubscriptionDashboardV2Enabled = yield select(isSubscriptionDashboardV2EnabledSelector);
      if (isSubscriptionDashboardV2Enabled) {
        yield call(getSubscriptionDashboardDataSaga);
      }
      yield getSolarProductsAvailabilitySaga();
    } catch (error) {
      logError('Error in loadOrderPageSaga', {
        error: error.message,
        errorCode: error.errorCode,
        isEditOrder,
      });
    }
  }
}

const clearedWallsSelection = { selectedWallsProduct: null, selectedWallsDeliveryProduct: null };

function getPrimaryProductList(userProducts, propertyType, isESXEnabled) {
  const notSetProducts = (userProducts?.NotSet?.PrimaryProducts || []).filter((p) => filterNotSetProducts(p, propertyType, isESXEnabled));
  const primaryList = userProducts?.[propertyType]?.PrimaryProducts ?? [];
  return [...primaryList, ...notSetProducts];
}

function getUpdatedSelectedWallsProduct(updatedState, orderState, primaryProductID) {
  const callerClearedWalls = Object.hasOwn(updatedState, 'selectedWallsProduct') && updatedState.selectedWallsProduct === null;
  if (callerClearedWalls) return clearedWallsSelection;

  const { selectedWallsProduct } = orderState;
  if (selectedWallsProduct && !selectedWallsProduct.CompatibleProducts.includes(primaryProductID)) {
    return clearedWallsSelection;
  }

  return {};
}

function isOtherProductCompatible(productList, productId, primaryProductID, isESXEnabled) {
  if (productId === PRODUCT_IDS.ESX && isESXEnabled) {
    return ESX_COMPATIBLE_PRODUCTS.includes(primaryProductID);
  }

  return (getProductFromId(productList, productId)?.CompatibleProducts ?? []).includes(primaryProductID);
}

function getUpdatedSelectedOtherProducts(updatedState, orderState, productList, primaryProductID, isESXEnabled) {
  const callerProvidedOtherProducts = Object.hasOwn(updatedState, 'selectedOtherProducts');
  if (callerProvidedOtherProducts) return updatedState.selectedOtherProducts;

  return Object.keys(orderState.selectedOtherProducts || {}).reduce((acc, selectedOtherProductId) => {
    const productId = Number(selectedOtherProductId);
    const isCompatible = isOtherProductCompatible(productList, productId, primaryProductID, isESXEnabled);

    if (isCompatible) {
      acc[selectedOtherProductId] = orderState.selectedOtherProducts[selectedOtherProductId];
    }
    return acc;
  }, {});
}

function buildSelectedPrimaryProductState(updatedState, orderState, userProducts, isESXEnabled) {
  const { selectedPrimaryProduct: { ProductID: primaryProductID } } = updatedState;
  const productList = getPrimaryProductList(userProducts, orderState.propertyType, isESXEnabled);
  const finalState = {
    ...updatedState,
    ...getDefaultMeasurementRequestTypes(primaryProductID),
    ...getUpdatedSelectedWallsProduct(updatedState, orderState, primaryProductID),
    selectedOtherProducts: getUpdatedSelectedOtherProducts(updatedState, orderState, productList, primaryProductID, isESXEnabled),
  };

  // Preserve delivery selection when caller provided it (subscription flow); normalize to number so Redux/store is consistent.
  if (Object.hasOwn(updatedState, 'selectedPrimaryDeliveryProduct')) {
    const v = updatedState.selectedPrimaryDeliveryProduct;
    finalState.selectedPrimaryDeliveryProduct = (v === null || v === undefined) ? v : Number(v);
  }

  return finalState;
}

function removeESXFromSelectedOtherProducts(selectedOtherProducts) {
  const updatedSelectedOtherProducts = { ...selectedOtherProducts };
  delete updatedSelectedOtherProducts[PRODUCT_IDS.ESX];
  return updatedSelectedOtherProducts;
}

function getStateWithESXCleared(updatedState, selectedOtherProducts, shouldClearESX) {
  if (!shouldClearESX) return updatedState;

  return {
    ...updatedState,
    selectedOtherProducts: removeESXFromSelectedOtherProducts(selectedOtherProducts),
  };
}

function shouldClearESXAfterPrimaryDeselected(orderState) {
  return !orderState.selectedWallsProduct
    || !ESX_COMPATIBLE_PRODUCTS.includes(orderState.selectedWallsProduct.ProductID);
}

function shouldClearESXAfterWallsDeselected(orderState) {
  return !orderState.selectedPrimaryProduct
    || !ESX_COMPATIBLE_PRODUCTS.includes(orderState.selectedPrimaryProduct.ProductID);
}

function hasSelectedESX(orderState) {
  return Boolean(orderState.selectedOtherProducts[PRODUCT_IDS.ESX]);
}

function* dispatchSelectedProduct(updatedState) {
  yield put({ type: types.SET_SELECTED_PRODUCT, updatedState });
}

export function* updateSelectedProductsSaga({ updatedState }) {
  const { order: orderState, userProducts } = yield select(({ order, userData }) => ({ order, userProducts: userData.userProducts }));
  const isESXEnabled = yield select(featureFlagSelector(ENABLE_GEOMNI_ESX_ADDON_LOGIC));

  if (updatedState.selectedPrimaryProduct) {
    yield dispatchSelectedProduct(buildSelectedPrimaryProductState(updatedState, orderState, userProducts, isESXEnabled));
    return;
  }

  if (isESXEnabled && updatedState.selectedPrimaryProduct === null && hasSelectedESX(orderState)) {
    yield dispatchSelectedProduct(getStateWithESXCleared(
      updatedState,
      orderState.selectedOtherProducts,
      shouldClearESXAfterPrimaryDeselected(orderState),
    ));
    return;
  }

  if (isESXEnabled && updatedState.selectedWallsProduct === null && hasSelectedESX(orderState)) {
    yield dispatchSelectedProduct(getStateWithESXCleared(
      updatedState,
      orderState.selectedOtherProducts,
      shouldClearESXAfterWallsDeselected(orderState),
    ));
    return;
  }

  yield dispatchSelectedProduct(updatedState);
}

export function* uploadBlueprintFileSaga({ file }) {
  try {
    const { t } = i18n;
    const fileName = file.name;

    if (file.type.match('application/pdf')) {
      yield put({ type: types.ORDER_PAGE_UPLOADING_BLUEPRINT_FILE });
      const requestBody = {
        fileType: FILE_TYPES.SubmittedBlueprint,
        fileFormat: FILE_FORMATS.PDF,
        validateFile: true,
        fileName,
        fileData: file,
      };
      const response = yield call(uploadResourceFileService, requestBody);

      if (!response.ok) {
        const error = new Error(response.json.errorDescription);
        error.errorCode = response.json.errorCode;
        throw error;
      }
      const { json: { fileId } } = response;
      const fileData = yield convertFileToBase64(file);

      yield put({
        type: types.ORDER_PAGE_UPLOAD_BLUEPRINT_FILE_SUCCESS, fileData, fileName, fileId,
      });
    } else {
      yield put({ type: types.ORDER_PAGE_UPLOAD_BLUEPRINT_FILE_ERROR, error: t('blueprint_order__unsupported_file_format') });
    }
  } catch (error) {
    logError({
      message: 'Error in uploadBlueprintFileSaga',
      error: error.message,
      errorCode: error.errorCode,
      fileName: file.name,
    });
    yield put({
      type: types.ORDER_PAGE_UPLOAD_BLUEPRINT_FILE_ERROR,
      error: error.errorCode === ErrorCodes.FileSizeExceeded ? error?.message : 'Internal Server Error',
    });
  }
}

function* updateImageCoverageIfApplicable() {
  const {
    myevRefreshImageAvailability,
    mapPoints,
    imageCoverageInfo,
  } = yield select(({ featureFlag, orderAddresses, userEntitlements }) => ({
    myevRefreshImageAvailability: featureFlag.myevRefreshImageAvailability,
    mapPoints: orderAddresses.mapPoints,
    imageCoverageInfo: orderAddresses.imageCoverageInfo,
    entitlements: userEntitlements,
  }));
  const isEntitledForImageAvailabilty = yield select(isEntitledForImageAvailabiltySelector);
  if (myevRefreshImageAvailability && isEntitledForImageAvailabilty // Check if the feature flag is enabled and customer has attribute enabled
    && mapPoints !== null && mapPoints.length !== 0 // If the map points are available
    && imageCoverageInfo // If the image coverage info is available
    && (mapPoints[0].address.latitude !== imageCoverageInfo.latitude || mapPoints[0].address.longitude !== imageCoverageInfo.longitude)) {
    yield call(checkImageCoverageSaga, { latitude: mapPoints[0].address.latitude, longitude: mapPoints[0].address.longitude });
  }
}

export function* deleteMapPointSaga({ deleteIndex }) {
  try {
    yield put(deleteMapPoint(deleteIndex));
    yield call(updateImageCoverageIfApplicable);
  } catch (error) {
    logError('Error in deleteMapPointSaga', {
      error: error.message,
      errorCode: error.errorCode,
      deleteIndex,
    });
  }
}

export function* updateMapPointAddressSaga({ updateIndex, updatedAddress, building }) {
  yield put(updateMapPointAddress(updateIndex, updatedAddress, building, true));
  yield call(updateImageCoverageIfApplicable);
}

export function* fetchAndUpdatePinAddressSaga({ pinIndex }) {
  try {
    const { address: { latitude, longitude } } = yield select(({ orderAddresses }) => orderAddresses.mapPoints[pinIndex]);

    // Set the address to loading
    yield put(updateMapPointLocation(pinIndex, {
      ...EMPTY_ADDRESS, address: LOADING_ADDRESS, latitude, longitude,
    }));

    // Fetch the address
    const { latitude: _, longitude: __, ...geocodedAddress } = yield select(({ orderAddresses }) => orderAddresses.geocodedAddress);
    const response = yield call(reverseGeocodeService, [latitude, longitude], geocodedAddress.country);
    if (!response.ok) {
      const error = new Error(response.json.errorDescription);
      error.errorCode = response.json.errorCode;
      throw error;
    }
    const { latitude: ___, longitude: ____, ...address } = getGeocodedAddressFromAddressResponse(response.json);

    // Update the address
    yield put(updateMapPointAddress(pinIndex, { ...geocodedAddress, ...address }));
  } catch (error) {
    logError({
      message: 'Error in fetchAndUpdatePinAddressSaga',
      error: error.message,
      errorCode: error.errorCode,
      pinIndex,
    });
    yield put(updateMapPointAddress(pinIndex, { ...EMPTY_ADDRESS, address: ERROR_LOADING_ADDRESS }));
  }
}

export function* updateMapPointLocationSaga({ updateIndex, newPinLocation: { latitude, longitude } }) {
  const myevAddressUpdateFeatureFlag = yield select(({ featureFlag }) => featureFlag.myevAddressUpdate);

  if (myevAddressUpdateFeatureFlag) {
    // Update the pin with loading address
    yield put(updateMapPointLocation(updateIndex, {
      ...EMPTY_ADDRESS, address: LOADING_ADDRESS, latitude, longitude,
    }));

    yield fetchAndUpdatePinAddressSaga({ pinIndex: updateIndex });
  } else {
    yield put(updateMapPointLocation(updateIndex, { latitude, longitude }));
  }

  yield call(updateImageCoverageIfApplicable);
}

export function* addPinSaga({ newPinLocation: { latitude, longitude } }) {
  const myevAddressUpdateFeatureFlag = yield select(({ featureFlag }) => featureFlag.myevAddressUpdate);

  if (myevAddressUpdateFeatureFlag) {
    // Get the index of the new pin
    const pinIndex = yield select(({ orderAddresses }) => orderAddresses.mapPoints?.length);

    // Add the pin with loading address
    yield put({
      type: types.ADD_MAP_PIN,
      address: {
        ...EMPTY_ADDRESS, address: LOADING_ADDRESS, latitude, longitude,
      },
    });

    yield fetchAndUpdatePinAddressSaga({ pinIndex });
  } else {
    const { latitude: _, longitude: __, ...geocodedAddress } = yield select(({ orderAddresses }) => orderAddresses.geocodedAddress);
    yield put({ type: types.ADD_MAP_PIN, address: { ...geocodedAddress, latitude, longitude } });
  }
}
